const BASE_URL = 'https://sentmo.com';
const normalizeLang = (lang) => (lang === 'en' ? 'en' : 'de');
const resolveLocale = (lang) => (lang === 'en' ? 'en-US' : 'de-DE');
const buildLangUrl = (lang, path = '') => {
  const base = `${BASE_URL}/${normalizeLang(lang)}`;
  if (!path) return base;
  return `${base}/${path.replace(/^\//, '')}`;
};

// Organization Schema
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  "name": "Sentmo",
  "url": BASE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": `${BASE_URL}/logo512.png`,
    "width": 512,
    "height": 512
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+49-176-41450810",
    "contactType": "customer service",
    "availableLanguage": ["German", "English"],
    "email": "business.pflueger@gmail.com"
  },
  "founder": {
    "@type": "Person",
    "name": "Sven Pflueger"
  },
  "foundingLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Schusterstrasse 40",
      "addressLocality": "Freiburg im Breisgau",
      "postalCode": "79098",
      "addressCountry": "DE"
    }
  }
};

// LocalBusiness Schema (for German local SEO)
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${BASE_URL}/#localbusiness`,
  "name": "Sentmo",
  "description": "Trading Sentiment Analyse Plattform fuer private Trader in Deutschland",
  "url": BASE_URL,
  "telephone": "+49-176-41450810",
  "email": "business.pflueger@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Schusterstrasse 40",
    "addressLocality": "Freiburg im Breisgau",
    "addressRegion": "Baden-Wuerttemberg",
    "postalCode": "79098",
    "addressCountry": "DE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 47.9990,
    "longitude": 7.8421
  },
  "areaServed": {
    "@type": "Country",
    "name": "Germany"
  },
  "priceRange": "$$"
};

// WebSite Schema
export const createWebsiteSchema = (lang = 'de') => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  "name": "Sentmo",
  "url": BASE_URL,
  "description": "Trading Sentiment Analyse Plattform fuer private Trader",
  "publisher": {
    "@id": `${BASE_URL}/#organization`
  },
  "inLanguage": resolveLocale(lang)
});

// SoftwareApplication Schema (for the trading tool)
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": `${BASE_URL}/#software`,
  "name": "Sentmo Trading Sentiment Tool",
  "description": "Echtzeit Trading Sentiment Analyse fuer US30, NASDAQ, S&P500, DAX und Gold. Taegliche Long/Short Positionierungsdaten.",
  "applicationCategory": "FinanceApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "EUR"
  },
  "author": {
    "@id": `${BASE_URL}/#organization`
  },
  "featureList": [
    "Daily sentiment analysis",
    "Long/Short position tracking",
    "24-hour change comparison",
    "Multiple instruments (US30, NASDAQ, S&P500, DAX, Gold)"
  ]
};

// BreadcrumbList Schema Generator
export const createBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

// WebPage Schema Generator
export const createWebPageSchema = (options) => {
  const { name, description, url, lang = 'de' } = options;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    "name": name,
    "description": description,
    "url": url,
    "isPartOf": {
      "@id": `${BASE_URL}/#website`
    },
    "about": {
      "@id": `${BASE_URL}/#organization`
    },
    "inLanguage": resolveLocale(lang)
  };
};

// Combined Schema for homepage
export const getHomePageStructuredData = ({ lang = 'de', title, description } = {}) => {
  const pageUrl = buildLangUrl(lang);
  const pageName = title || "Sentmo - Trading Sentiment Analyse";
  const pageDescription = description || "Vereinfachen Sie Ihr Trading mit Sentmo. Taegliche Sentiment-Analysen fuer US30, NASDAQ, S&P500, DAX und Gold.";
  return [
    organizationSchema,
    localBusinessSchema,
    createWebsiteSchema(lang),
    softwareApplicationSchema,
    createWebPageSchema({
      name: pageName,
      description: pageDescription,
      url: pageUrl,
      lang
    }),
    createBreadcrumbSchema([
      { name: "Home", url: pageUrl }
    ])
  ];
};

// Sentiment page schema
export const getSentimentPageStructuredData = ({ lang = 'de', title, description } = {}) => {
  const pageUrl = buildLangUrl(lang, 'sentiment');
  const pageName = title || "Daily Sentiment - Sentmo";
  const pageDescription = description || "Taegliche Trading Sentiment Analyse mit Long/Short Positionierungsdaten fuer US30, NASDAQ, S&P500, DAX und Gold.";
  return [
    createWebPageSchema({
      name: pageName,
      description: pageDescription,
      url: pageUrl,
      lang
    }),
    createBreadcrumbSchema([
      { name: "Home", url: buildLangUrl(lang) },
      { name: "Sentiment", url: pageUrl }
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Dataset",
      "name": "Trading Sentiment Data",
      "description": "Real-time long/short position data for major trading instruments",
      "creator": {
        "@id": `${BASE_URL}/#organization`
      },
      "temporalCoverage": "Daily updates",
      "inLanguage": resolveLocale(lang)
    }
  ];
};

// Imprint page schema
export const getImprintPageStructuredData = ({ lang = 'de', title, description } = {}) => {
  const pageUrl = buildLangUrl(lang, 'imprint');
  const pageName = title || "Impressum - Sentmo";
  const pageDescription = description || "Impressum und rechtliche Informationen gemaess Paragraph 5 DDG fuer Sentmo.";
  return [
    createWebPageSchema({
      name: pageName,
      description: pageDescription,
      url: pageUrl,
      lang
    }),
    createBreadcrumbSchema([
      { name: "Home", url: buildLangUrl(lang) },
      { name: "Impressum", url: pageUrl }
    ])
  ];
};

// Data Protection page schema
export const getDataProtectionPageStructuredData = ({ lang = 'de', title, description } = {}) => {
  const pageUrl = buildLangUrl(lang, 'dataprotection');
  const pageName = title || "Datenschutzerklaerung - Sentmo";
  const pageDescription = description || "Datenschutzerklaerung und Informationen zum Datenschutz bei Sentmo gemaess DSGVO.";
  return [
    createWebPageSchema({
      name: pageName,
      description: pageDescription,
      url: pageUrl,
      lang
    }),
    createBreadcrumbSchema([
      { name: "Home", url: buildLangUrl(lang) },
      { name: "Datenschutz", url: pageUrl }
    ])
  ];
};

// FAQ Schema Generator
export const createFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Trading FAQ Data
export const tradingFAQs = [
  {
    question: "Was ist Trading Sentiment Analyse?",
    answer: "Trading Sentiment Analyse ist eine Methode zur Messung der Marktstimmung. Sie zeigt, wie viele Trader long (auf steigende Kurse) oder short (auf fallende Kurse) positioniert sind. Diese Daten helfen, die Marktpsychologie zu verstehen und bessere Trading-Entscheidungen zu treffen."
  },
  {
    question: "Wie funktioniert die Sentmo Sentiment Analyse?",
    answer: "Sentmo sammelt taegliche Positionierungsdaten von Retail-Tradern fuer verschiedene Instrumente wie US30, NASDAQ, S&P500, DAX und Gold. Die Daten werden als Prozentsatz der Long- und Short-Positionen angezeigt, zusammen mit der 24-Stunden-Veraenderung."
  },
  {
    question: "Welche Instrumente werden bei Sentmo analysiert?",
    answer: "Sentmo bietet Sentiment-Daten fuer fuenf wichtige Handelsinstrumente: US30 (Dow Jones), NASDAQ, SPX500 (S&P 500), GER30 (DAX) und Gold. Diese decken die wichtigsten Aktienindizes und Rohstoffe ab."
  },
  {
    question: "Ist Sentmo kostenlos?",
    answer: "Ja, die taegliche Sentiment-Analyse auf Sentmo ist kostenlos verfuegbar. Sie koennen die aktuellen Long/Short-Positionierungen und deren Veraenderungen ohne Registrierung einsehen."
  },
  {
    question: "Wie oft werden die Sentiment-Daten aktualisiert?",
    answer: "Die Sentiment-Daten werden taeglich aktualisiert. Die Anzeige zeigt sowohl die aktuellen Werte als auch die Veraenderung gegenueber dem Vortag, sodass Sie Trends in der Marktstimmung erkennen koennen."
  },
  {
    question: "Was bedeutet es, wenn viele Trader long sind?",
    answer: "Wenn ein hoher Prozentsatz der Trader long positioniert ist, erwarten diese steigende Kurse. Dies kann jedoch auch als Kontraindikator dienen - extreme Positionierungen deuten oft auf moegliche Trendwenden hin, da die Masse haeufig falsch liegt."
  },
  {
    question: "Fuer wen ist Sentmo geeignet?",
    answer: "Sentmo richtet sich an private Trader (Retail Trader) aller Erfahrungsstufen. Ob Anfaenger oder erfahrener Trader - die Sentiment-Daten bieten wertvolle Einblicke in die Marktpsychologie und koennen die eigene Trading-Strategie unterstuetzen."
  },
  {
    question: "Was ist ein Trading Journal und warum ist es wichtig?",
    answer: "Ein Trading Journal ist ein Werkzeug zur Dokumentation und Analyse Ihrer Trades. Es hilft, Muster zu erkennen, Fehler zu identifizieren und die eigene Trading-Strategie kontinuierlich zu verbessern. Sentmo plant, ein integriertes Trading Journal anzubieten."
  }
];

// Get FAQ structured data - accepts FAQs array from locale
export const getFAQStructuredData = (faqs) => {
  // If faqs provided, use them; otherwise fall back to tradingFAQs
  const faqData = faqs || tradingFAQs;
  return createFAQSchema(faqData);
};

// Blog Article Schema Generator
export const createArticleSchema = (article) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image || `${BASE_URL}/og-image.jpg`,
    "author": {
      "@type": "Person",
      "name": "Sven Pflueger"
    },
    "publisher": {
      "@id": `${BASE_URL}/#organization`
    },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": article.url
    },
    "inLanguage": article.lang ? resolveLocale(article.lang) : undefined
  };
};

// Blog page schema
export const getBlogPageStructuredData = ({ lang = 'de', title, description, posts = [] } = {}) => {
  const pageUrl = buildLangUrl(lang, 'blog');
  const pageName = title || "Trading Blog - Sentmo";
  const pageDescription = description || "Trading Wissen, Marktanalysen und Tipps fuer erfolgreiche Trader. Lernen Sie Trading Sentiment Analyse und verbessern Sie Ihre Strategie.";
  const listItems = posts.map((post, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": post.title,
    "url": `${pageUrl}/${post.slug}`
  }));
  const itemListSchema = listItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": listItems
  } : null;
  return [
    createWebPageSchema({
      name: pageName,
      description: pageDescription,
      url: pageUrl,
      lang
    }),
    createBreadcrumbSchema([
      { name: "Home", url: buildLangUrl(lang) },
      { name: "Blog", url: pageUrl }
    ]),
    ...(itemListSchema ? [itemListSchema] : [])
  ];
};
